import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { UserPlus, Trash2, Mail, Loader2 } from "lucide-react";

export default function AdminStaff() {
  const [staff, setStaff] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/staff").then(({ data }) => setStaff(data)).catch(() => setStaff([]));
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.name || !form.email || form.password.length < 6) {
      toast.error("Enter a name, email and a password of at least 6 characters.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/staff", { ...form, email: form.email.trim().toLowerCase() });
      toast.success("Staff account created");
      setForm({ name: "", email: "", password: "" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/staff/${id}`);
      toast.success("Staff removed");
      load();
    } catch {
      toast.error("Could not remove staff");
    }
  };

  if (staff === null)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Admin</p>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight">Staff accounts</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-staff-button" className="rounded-xl hover:bg-primary/90 active:scale-[0.98]">
              <UserPlus className="mr-1.5 h-4 w-4" /> Add staff
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading">New staff member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label>Full name</Label>
                <Input
                  data-testid="staff-name-input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Doe"
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input
                  data-testid="staff-email-input"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@shadyacrescaravanpark.com.au"
                  className="h-11 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Temporary password</Label>
                <Input
                  data-testid="staff-password-input"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="At least 6 characters"
                  className="h-11 rounded-xl"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="save-staff-button"
                className="w-full rounded-xl hover:bg-primary/90"
                disabled={saving}
                onClick={create}
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Create account"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {staff.length === 0 ? (
        <p className="rounded-2xl border border-border bg-card py-12 text-center text-sm text-muted-foreground">
          No staff yet. Add your first team member.
        </p>
      ) : (
        <div className="grid gap-3">
          {staff.map((s) => (
            <div
              key={s.id}
              data-testid={`staff-row-${s.id}`}
              className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4 shadow-[0_8px_30px_rgba(37,75,54,0.06)]"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-secondary font-heading font-bold text-primary">
                {s.name.charAt(0).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{s.name}</p>
                <p className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                  <Mail className="h-3 w-3" /> {s.email}
                </p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    data-testid={`delete-staff-${s.id}`}
                    variant="ghost"
                    size="icon"
                    className="rounded-full text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="rounded-2xl">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-heading">Remove {s.name}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This removes their login. Their past training attempts stay in the records.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      data-testid={`confirm-delete-staff-${s.id}`}
                      className="rounded-xl bg-destructive hover:bg-destructive/90"
                      onClick={() => remove(s.id)}
                    >
                      Remove
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
